import React, { useState, useRef } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { 
  QrCode, Download, Printer, Wifi, DollarSign, 
  Type, Copy, UtensilsCrossed, Palette, 
  Eye, EyeOff, LayoutTemplate, Tag, UploadCloud,
  Trash2, Image as ImageIcon, Check
} from 'lucide-react';
import toast from 'react-hot-toast';

// Componente Switch Toggle Moderno
const SwitchToggle = ({ checked, onChange, label, description, icon }) => (
  <div 
    onClick={() => onChange(!checked)}
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '12px 14px',
      backgroundColor: '#f8fafc',
      borderRadius: '10px',
      border: '1px solid #e2e8f0',
      cursor: 'pointer',
      userSelect: 'none',
      transition: 'all 0.2s ease'
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
      {icon && (
        <div style={{ 
          width: '32px', 
          height: '32px', 
          borderRadius: '8px', 
          backgroundColor: checked ? '#eff6ff' : '#f1f5f9',
          color: checked ? '#2563eb' : '#64748b',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.2s'
        }}>
          {icon}
        </div>
      )}
      <div>
        <div style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{label}</div>
        {description && <div style={{ fontSize: '11px', color: '#64748b', marginTop: '1px' }}>{description}</div>}
      </div>
    </div>

    {/* O botão Switch em si */}
    <div style={{
      position: 'relative',
      width: '44px',
      height: '24px',
      backgroundColor: checked ? '#2563eb' : '#cbd5e1',
      borderRadius: '24px',
      transition: 'background-color 0.25s ease',
      flexShrink: 0
    }}>
      <div style={{
        position: 'absolute',
        top: '3px',
        left: checked ? '23px' : '3px',
        width: '18px',
        height: '18px',
        backgroundColor: '#ffffff',
        borderRadius: '50%',
        boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
        transition: 'left 0.25s cubic-bezier(0.4, 0, 0.2, 1)'
      }} />
    </div>
  </div>
);

const QRCodeManager = ({ lojaInfo }) => {
  const [tipo, setTipo] = useState('cardapio'); // 'cardapio' | 'pix' | 'wifi' | 'custom'
  const [modoVisualizacao, setModoVisualizacao] = useState('display'); // 'display' | 'qr-apenas' | 'etiquetas'
  const qrCanvasRef = useRef(null);
  const fileBgRef = useRef(null);
  const fileLogoRef = useRef(null);

  // Dados do QR Code
  const [numeroMesa, setNumeroMesa] = useState('');
  const [pixChave, setPixChave] = useState('');
  const [pixNome, setPixNome] = useState('');
  const [wifiSsid, setWifiSsid] = useState('');
  const [wifiSenha, setWifiSenha] = useState('');
  const [wifiTipo, setWifiTipo] = useState('WPA');
  const [mostrarTextoWifi, setMostrarTextoWifi] = useState(true);
  const [textoLivre, setTextoLivre] = useState('');

  // Configuração das Etiquetas
  const [quantidadeEtiquetas, setQuantidadeEtiquetas] = useState(12);
  const [tamanhoEtiqueta, setTamanhoEtiqueta] = useState(80);

  // Personalização Visual da Placa
  const [formato, setFormato] = useState('mesa'); // 'mesa' (10x15cm) | 'a4' (A4 vertical)
  const [tituloDisplay, setTituloDisplay] = useState('');
  const [subtituloDisplay, setSubtituloDisplay] = useState('');
  const [rodapeDisplay, setRodapeDisplay] = useState('Aponte a câmera do seu celular para escanear');
  
  // Cores e Transparência
  const [fundoTransparente, setFundoTransparente] = useState(false);
  const [fundoQrTransparente, setFundoQrTransparente] = useState(false);
  const [corFundo, setCorFundo] = useState('#ffffff');
  const [corPrimaria, setCorPrimaria] = useState('#1e293b');
  const [corTexto, setCorTexto] = useState('#475569');
  const [corQrCode, setCorQrCode] = useState('#000000');
  const [corFundoQr, setCorFundoQr] = useState('#ffffff');
  const [bgImagemUrl, setBgImagemUrl] = useState('');

  // Logo
  const BASE_URL = 'https://app.universomidiadigital.com.br';
  const logoPadrao = lojaInfo?.logo_url?.startsWith('http')
    ? lojaInfo.logo_url
    : lojaInfo?.logo_url ? `${BASE_URL}${lojaInfo.logo_url}` : '';

  const [customLogo, setCustomLogo] = useState('');
  const [incluirLogo, setIncluirLogo] = useState(true);

  const logoFinal = customLogo || logoPadrao;

  // Monta a URL do cardápio público
  const getUrlCardapio = () => {
    if (!lojaInfo?.slug) return 'https://app.universomidiadigital.com.br';
    const baseUrl = `https://app.universomidiadigital.com.br/${lojaInfo.slug}`;
    return numeroMesa ? `${baseUrl}?mesa=${encodeURIComponent(numeroMesa)}` : baseUrl;
  };

  // Valor textual do QR Code
  const getQRCodeValue = () => {
    switch (tipo) {
      case 'cardapio':
        return getUrlCardapio();
      case 'wifi':
        if (!wifiSsid) return 'WIFI:S:Exemplo;T:WPA;P:12345678;;';
        return `WIFI:S:${wifiSsid};T:${wifiTipo};P:${wifiSenha};;`;
      case 'pix':
        return pixChave || 'https://pix.bcb.gov.br';
      case 'custom':
        return textoLivre || 'https://app.universomidiadigital.com.br';
      default:
        return 'https://app.universomidiadigital.com.br';
    }
  };

  const qrValor = getQRCodeValue();

  const getTituloExibicao = () => {
    if (tituloDisplay) return tituloDisplay;
    if (tipo === 'cardapio') return numeroMesa ? `MESA ${numeroMesa}` : (lojaInfo?.nome || 'CARDÁPIO DIGITAL');
    if (tipo === 'wifi') return 'WI-FI GRÁTIS';
    if (tipo === 'pix') return 'PAGAMENTO PIX';
    return lojaInfo?.nome || 'ACESSE AQUI';
  };

  const getSubtituloExibicao = () => {
    if (subtituloDisplay) return subtituloDisplay;
    if (tipo === 'cardapio') return 'Aponte a câmera do seu celular para ver o cardápio e fazer seu pedido';
    if (tipo === 'wifi') return 'Aponte a câmera para se conectar automaticamente à nossa rede';
    if (tipo === 'pix') return pixNome ? `Beneficiário: ${pixNome}` : 'Abra o app do seu banco e escaneie o código';
    return 'Aponte a câmera do celular para ler o QR Code';
  };

  const handleUploadBg = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => setBgImagemUrl(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleUploadLogo = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setCustomLogo(reader.result);
        setIncluirLogo(true);
      };
      reader.readAsDataURL(file);
    }
  };

  // Download do PNG
  const baixarApenasPNG = () => {
    const canvas = qrCanvasRef.current?.querySelector('canvas');
    if (!canvas) {
      toast.error('Gere um QR Code válido primeiro.');
      return;
    }
    const url = canvas.toDataURL('image/png');
    const link = document.createElement('a');
    link.href = url;
    link.download = `qrcode-${tipo}-${fundoQrTransparente ? 'transparente-' : ''}${numeroMesa ? `mesa-${numeroMesa}-` : ''}${lojaInfo?.slug || 'loja'}.png`;
    link.click();
    toast.success(fundoQrTransparente ? 'QR Code transparente baixado!' : 'QR Code baixado com sucesso!');
  };

  // Impressão Isolada sem erros de tela em branco
  const handleImprimir = () => {
    const canvas = qrCanvasRef.current?.querySelector('canvas');
    if (!canvas) {
      toast.error('Aguarde a renderização do QR Code para imprimir.');
      return;
    }

    const qrImageData = canvas.toDataURL('image/png');
    const printWindow = window.open('', '_blank', 'width=900,height=950');
    if (!printWindow) {
      toast.error('Permita a abertura de pop-ups no navegador para imprimir.');
      return;
    }

    let htmlConteudo = '';

    if (modoVisualizacao === 'qr-apenas') {
      htmlConteudo = `
        <div style="text-align: center; padding: 40px;">
          <img src="${qrImageData}" style="width: 280px; height: 280px; display: block; margin: 0 auto;" />
          <h2 style="font-size: 18px; color: #1e293b; margin-top: 16px; font-weight: 700;">${getTituloExibicao()}</h2>
        </div>
      `;
    } else if (modoVisualizacao === 'etiquetas') {
      const listaEtiquetas = Array.from({ length: quantidadeEtiquetas });
      htmlConteudo = `
        <div style="
          display: grid; 
          grid-template-columns: repeat(auto-fill, minmax(130px, 1fr)); 
          gap: 16px; 
          padding: 10px;
          width: 100%;
          box-sizing: border-box;
        ">
          ${listaEtiquetas.map(() => `
            <div style="
              border: 1px dashed #94a3b8; 
              border-radius: 10px; 
              padding: 10px; 
              text-align: center; 
              background: #fff;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              page-break-inside: avoid;
            ">
              ${logoFinal ? `<img src="${logoFinal}" style="max-height: 20px; max-width: 60px; object-fit: contain; margin-bottom: 4px;" />` : ''}
              <div style="font-size: 11px; font-weight: 800; color: #0f172a; margin-bottom: 4px; text-transform: uppercase;">
                ${getTituloExibicao()}
              </div>
              <img src="${qrImageData}" style="width: ${tamanhoEtiqueta}px; height: ${tamanhoEtiqueta}px; display: block; margin: 0 auto;" />
              <div style="font-size: 9px; color: #64748b; margin-top: 4px; font-weight: 600;">
                Escaneie Aqui
              </div>
            </div>
          `).join('')}
        </div>
      `;
    } else {
      htmlConteudo = `
        <div style="
          width: ${formato === 'mesa' ? '360px' : '580px'};
          min-height: ${formato === 'mesa' ? '490px' : '720px'};
          background-color: ${fundoTransparente ? 'transparent' : corFundo};
          background-image: ${!fundoTransparente && bgImagemUrl ? `url(${bgImagemUrl})` : 'none'};
          background-size: cover;
          background-position: center;
          border: ${fundoTransparente ? '2px dashed #94a3b8' : `3px solid ${corPrimaria}`};
          border-radius: 20px;
          padding: 30px 24px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: space-between;
          text-align: center;
          box-sizing: border-box;
        ">
          <div style="width: 100%;">
            ${logoFinal ? `<img src="${logoFinal}" style="max-height: ${formato === 'mesa' ? '46px' : '65px'}; max-width: 160px; object-fit: contain; margin: 0 auto 12px auto; display: block;" />` : ''}
            <h1 style="font-size: ${formato === 'mesa' ? '22px' : '28px'}; font-weight: 900; color: ${corPrimaria}; margin: 0 0 6px 0; text-transform: uppercase; letter-spacing: 0.5px;">
              ${getTituloExibicao()}
            </h1>
            <p style="font-size: ${formato === 'mesa' ? '13px' : '15px'}; color: ${corTexto}; margin: 0 auto; max-width: 300px; line-height: 1.35; font-weight: 500;">
              ${getSubtituloExibicao()}
            </p>
          </div>

          <div style="
            padding: 14px;
            background: ${fundoQrTransparente ? 'transparent' : corFundoQr};
            border-radius: 16px;
            border: ${fundoQrTransparente ? 'none' : '1px solid #e2e8f0'};
            display: inline-block;
            margin: 20px 0;
          ">
            <img src="${qrImageData}" style="width: ${formato === 'mesa' ? '190px' : '240px'}; height: ${formato === 'mesa' ? '190px' : '240px'}; display: block;" />
          </div>

          <div style="width: 100%;">
            ${tipo === 'wifi' && mostrarTextoWifi && wifiSsid ? `
              <div style="
                margin-bottom: 12px;
                padding: 8px 16px;
                background: rgba(255, 255, 255, 0.95);
                border-radius: 8px;
                border: 1px solid #cbd5e1;
                font-size: 13px;
                color: #0f172a;
                display: inline-block;
              ">
                <div><strong>Rede:</strong> ${wifiSsid}</div>
                ${wifiSenha ? `<div><strong>Senha:</strong> ${wifiSenha}</div>` : ''}
              </div>
            ` : ''}

            <div style="font-size: 11px; color: ${corTexto}; font-weight: 600; opacity: 0.9;">
              ${rodapeDisplay}
            </div>

            <div style="margin-top: 12px; border-top: 1px dashed rgba(0,0,0,0.15); padding-top: 8px; font-size: 10px; color: ${corTexto}; opacity: 0.65;">
              ${lojaInfo?.nome || 'Universo Mídia Digital'}
            </div>
          </div>
        </div>
      `;
    }

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Impressão - ${getTituloExibicao()}</title>
          <style>
            @page {
              size: auto;
              margin: 8mm;
            }
            * {
              box-sizing: border-box;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            body {
              margin: 0;
              padding: 0;
              background: #fff;
              display: flex;
              align-items: center;
              justify-content: center;
              min-height: 100vh;
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
            }
          </style>
        </head>
        <body>
          ${htmlConteudo}
          <script>
            window.onload = function() {
              window.focus();
              window.print();
              window.close();
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const copiarConteudo = () => {
    if (!qrValor) return;
    navigator.clipboard.writeText(qrValor);
    toast.success('Link / Conteúdo copiado!');
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1250px', margin: '0 auto', fontFamily: 'inherit' }}>
      
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '10px', margin: 0 }}>
          <QrCode size={28} color="#2563eb" /> Gerador & Criador de Displays e Etiquetas
        </h2>
        <p style={{ color: '#64748b', fontSize: '14px', marginTop: '6px' }}>
          Crie placas de mesa, folhas de etiquetas adesivas, displays para balcão, Wi-Fi e Pix com suporte a fundo transparente.
        </p>
      </div>

      {/* Navegação por Tipo de Dados */}
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '20px' }}>
        {[
          { id: 'cardapio', label: 'Cardápio / Mesas', icon: <UtensilsCrossed size={16} /> },
          { id: 'wifi', label: 'Wi-Fi do Estabelecimento', icon: <Wifi size={16} /> },
          { id: 'pix', label: 'Pagamento Pix', icon: <DollarSign size={16} /> },
          { id: 'custom', label: 'Link / Texto Livre', icon: <Type size={16} /> }
        ].map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => {
              setTipo(item.id);
              setTituloDisplay('');
              setSubtituloDisplay('');
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 18px',
              borderRadius: '8px',
              border: tipo === item.id ? '2px solid #2563eb' : '1px solid #cbd5e1',
              backgroundColor: tipo === item.id ? '#eff6ff' : '#ffffff',
              color: tipo === item.id ? '#2563eb' : '#475569',
              fontWeight: tipo === item.id ? '700' : '500',
              cursor: 'pointer',
              transition: 'all 0.15s ease'
            }}
          >
            {item.icon} {item.label}
          </button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: '28px', alignItems: 'start' }}>
        
        {/* COLUNA ESQUERDA: Formulários e Ajustes */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          
          {/* Bloco 1: Conteúdo do QR */}
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#1e293b', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <QrCode size={18} color="#2563eb" /> 1. Dados do QR Code
            </h3>

            {tipo === 'cardapio' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
                    Identificação da Mesa / Local (Opcional)
                  </label>
                  <input
                    type="text"
                    placeholder="Ex: 01, 12, Balcão, Varanda"
                    value={numeroMesa}
                    onChange={(e) => setNumeroMesa(e.target.value)}
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
                    Link Gerado
                  </label>
                  <input
                    type="text"
                    readOnly
                    value={getUrlCardapio()}
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', backgroundColor: '#f8fafc', color: '#475569', boxSizing: 'border-box' }}
                  />
                </div>
              </div>
            )}

            {tipo === 'wifi' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>Nome da Rede Wi-Fi (SSID)</label>
                  <input
                    type="text"
                    placeholder="Ex: MinhaLoja_Clientes"
                    value={wifiSsid}
                    onChange={(e) => setWifiSsid(e.target.value)}
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>Senha do Wi-Fi</label>
                  <input
                    type="text"
                    placeholder="Digite a senha da rede"
                    value={wifiSenha}
                    onChange={(e) => setWifiSenha(e.target.value)}
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>Criptografia</label>
                  <select
                    value={wifiTipo}
                    onChange={(e) => setWifiTipo(e.target.value)}
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}
                  >
                    <option value="WPA">WPA / WPA2 / WPA3 (Padrão)</option>
                    <option value="WEP">WEP</option>
                    <option value="nopass">Sem Senha (Rede Aberta)</option>
                  </select>
                </div>

                {/* Switch Exibir/Ocultar Texto Wi-Fi */}
                <SwitchToggle
                  checked={mostrarTextoWifi}
                  onChange={setMostrarTextoWifi}
                  label="Exibir SSID e Senha em texto"
                  description={mostrarTextoWifi ? "Nome da rede e senha visíveis na placa" : "Apenas o QR Code fará a conexão direta"}
                  icon={mostrarTextoWifi ? <Eye size={16} /> : <EyeOff size={16} />}
                />
              </div>
            )}

            {tipo === 'pix' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>Nome do Recebedor</label>
                  <input
                    type="text"
                    placeholder="Ex: Seu Estabelecimento"
                    value={pixNome}
                    onChange={(e) => setPixNome(e.target.value)}
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>Código Pix / Chave</label>
                  <textarea
                    rows={3}
                    placeholder="Cole aqui a chave Pix ou payload copia-e-cola"
                    value={pixChave}
                    onChange={(e) => setPixChave(e.target.value)}
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}
                  />
                </div>
              </div>
            )}

            {tipo === 'custom' && (
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>Texto ou Link</label>
                <input
                  type="text"
                  placeholder="https://exemplo.com ou texto livre"
                  value={textoLivre}
                  onChange={(e) => setTextoLivre(e.target.value)}
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}
                />
              </div>
            )}
          </div>

          {/* Bloco 2: Modo de Apresentação e Impressão */}
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#1e293b', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Palette size={18} color="#2563eb" /> 2. Formato & Personalização
            </h3>

            {/* Seletor de Modo: Placa / Apenas QR / Folha de Etiquetas */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '16px' }}>
              <button
                type="button"
                onClick={() => setModoVisualizacao('display')}
                style={{
                  padding: '10px 6px',
                  borderRadius: '8px',
                  border: modoVisualizacao === 'display' ? '2px solid #2563eb' : '1px solid #cbd5e1',
                  backgroundColor: modoVisualizacao === 'display' ? '#eff6ff' : '#fff',
                  color: modoVisualizacao === 'display' ? '#2563eb' : '#475569',
                  fontWeight: '600',
                  fontSize: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <LayoutTemplate size={16} />
                Placa Display
              </button>

              <button
                type="button"
                onClick={() => setModoVisualizacao('etiquetas')}
                style={{
                  padding: '10px 6px',
                  borderRadius: '8px',
                  border: modoVisualizacao === 'etiquetas' ? '2px solid #2563eb' : '1px solid #cbd5e1',
                  backgroundColor: modoVisualizacao === 'etiquetas' ? '#eff6ff' : '#fff',
                  color: modoVisualizacao === 'etiquetas' ? '#2563eb' : '#475569',
                  fontWeight: '600',
                  fontSize: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <Tag size={16} />
                Folha Etiquetas
              </button>

              <button
                type="button"
                onClick={() => setModoVisualizacao('qr-apenas')}
                style={{
                  padding: '10px 6px',
                  borderRadius: '8px',
                  border: modoVisualizacao === 'qr-apenas' ? '2px solid #2563eb' : '1px solid #cbd5e1',
                  backgroundColor: modoVisualizacao === 'qr-apenas' ? '#eff6ff' : '#fff',
                  color: modoVisualizacao === 'qr-apenas' ? '#2563eb' : '#475569',
                  fontWeight: '600',
                  fontSize: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <QrCode size={16} />
                Apenas QR
              </button>
            </div>

            {/* Opções específicas para ETIQUETAS */}
            {modoVisualizacao === 'etiquetas' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '14px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
                    Quantidade por Folha A4
                  </label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {[6, 12, 24, 30].map(qty => (
                      <button
                        key={qty}
                        type="button"
                        onClick={() => {
                          setQuantidadeEtiquetas(qty);
                          if (qty >= 24) setTamanhoEtiqueta(65);
                          else setTamanhoEtiqueta(80);
                        }}
                        style={{
                          flex: 1,
                          padding: '6px 0',
                          borderRadius: '6px',
                          border: quantidadeEtiquetas === qty ? '2px solid #2563eb' : '1px solid #cbd5e1',
                          backgroundColor: quantidadeEtiquetas === qty ? '#eff6ff' : '#fff',
                          color: quantidadeEtiquetas === qty ? '#2563eb' : '#475569',
                          fontWeight: '700',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        {qty} un
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '4px' }}>
                    Título na Etiqueta
                  </label>
                  <input
                    type="text"
                    placeholder={getTituloExibicao()}
                    value={tituloDisplay}
                    onChange={(e) => setTituloDisplay(e.target.value)}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}
                  />
                </div>
              </div>
            )}

            {/* Opções específicas para DISPLAY */}
            {modoVisualizacao === 'display' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>Tamanho da Placa</label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    <button
                      type="button"
                      onClick={() => setFormato('mesa')}
                      style={{
                        padding: '8px 12px',
                        borderRadius: '6px',
                        border: formato === 'mesa' ? '2px solid #2563eb' : '1px solid #cbd5e1',
                        backgroundColor: formato === 'mesa' ? '#eff6ff' : '#fff',
                        color: formato === 'mesa' ? '#2563eb' : '#475569',
                        fontWeight: '600',
                        cursor: 'pointer',
                        fontSize: '13px'
                      }}
                    >
                      Mesa (10x15cm)
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormato('a4')}
                      style={{
                        padding: '8px 12px',
                        borderRadius: '6px',
                        border: formato === 'a4' ? '2px solid #2563eb' : '1px solid #cbd5e1',
                        backgroundColor: formato === 'a4' ? '#eff6ff' : '#fff',
                        color: formato === 'a4' ? '#2563eb' : '#475569',
                        fontWeight: '600',
                        cursor: 'pointer',
                        fontSize: '13px'
                      }}
                    >
                      Totem / Folha A4
                    </button>
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '4px' }}>Título Principal</label>
                  <input
                    type="text"
                    placeholder={getTituloExibicao()}
                    value={tituloDisplay}
                    onChange={(e) => setTituloDisplay(e.target.value)}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '4px' }}>Subtítulo / Instrução</label>
                  <input
                    type="text"
                    placeholder={getSubtituloExibicao()}
                    value={subtituloDisplay}
                    onChange={(e) => setSubtituloDisplay(e.target.value)}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '4px' }}>Rodapé</label>
                  <input
                    type="text"
                    value={rodapeDisplay}
                    onChange={(e) => setRodapeDisplay(e.target.value)}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}
                  />
                </div>
              </div>
            )}

            {/* CONTROLES DE TRANSPARÊNCIA & CORES (Com Switches) */}
            <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '14px', marginTop: '12px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#334155', marginBottom: '10px' }}>
                Transparência & Cores
              </label>

              {/* Switches Modernos de Transparência */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '14px' }}>
                <SwitchToggle
                  checked={fundoQrTransparente}
                  onChange={setFundoQrTransparente}
                  label="Fundo Transparente no QR Code"
                  description="Remove o quadrado branco de fundo do QR Code"
                  icon={<QrCode size={16} />}
                />

                {modoVisualizacao === 'display' && (
                  <SwitchToggle
                    checked={fundoTransparente}
                    onChange={setFundoTransparente}
                    label="Fundo Transparente na Placa"
                    description="Ideal para displays em acrílico ou adesivo transparente"
                    icon={<LayoutTemplate size={16} />}
                  />
                )}
              </div>

              {/* Seletor de Cores */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '10px' }}>
                {!fundoTransparente && modoVisualizacao === 'display' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <input type="color" value={corFundo} onChange={(e) => setCorFundo(e.target.value)} style={{ width: '32px', height: '32px', borderRadius: '6px', border: '1px solid #cbd5e1', cursor: 'pointer' }} />
                    <span style={{ fontSize: '12px', color: '#475569' }}>Fundo Card</span>
                  </div>
                )}

                {modoVisualizacao === 'display' && (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <input type="color" value={corPrimaria} onChange={(e) => setCorPrimaria(e.target.value)} style={{ width: '32px', height: '32px', borderRadius: '6px', border: '1px solid #cbd5e1', cursor: 'pointer' }} />
                      <span style={{ fontSize: '12px', color: '#475569' }}>Título/Borda</span>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <input type="color" value={corTexto} onChange={(e) => setCorTexto(e.target.value)} style={{ width: '32px', height: '32px', borderRadius: '6px', border: '1px solid #cbd5e1', cursor: 'pointer' }} />
                      <span style={{ fontSize: '12px', color: '#475569' }}>Textos</span>
                    </div>
                  </>
                )}

                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <input type="color" value={corQrCode} onChange={(e) => setCorQrCode(e.target.value)} style={{ width: '32px', height: '32px', borderRadius: '6px', border: '1px solid #cbd5e1', cursor: 'pointer' }} />
                  <span style={{ fontSize: '12px', color: '#475569' }}>Cor QR Code</span>
                </div>

                {!fundoQrTransparente && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <input type="color" value={corFundoQr} onChange={(e) => setCorFundoQr(e.target.value)} style={{ width: '32px', height: '32px', borderRadius: '6px', border: '1px solid #cbd5e1', cursor: 'pointer' }} />
                    <span style={{ fontSize: '12px', color: '#475569' }}>Fundo QR</span>
                  </div>
                )}
              </div>
            </div>

            {/* Uploads Formatados com Dropzone / Botões Elegantes */}
            <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '16px', marginTop: '14px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              
              {/* Upload da Imagem de Fundo */}
              {modoVisualizacao === 'display' && !fundoTransparente && (
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
                    Imagem de Fundo da Placa
                  </label>
                  
                  <input 
                    type="file" 
                    ref={fileBgRef} 
                    accept="image/*" 
                    onChange={handleUploadBg} 
                    style={{ display: 'none' }} 
                  />

                  {bgImagemUrl ? (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '8px 12px',
                      background: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <img src={bgImagemUrl} alt="Bg Preview" style={{ width: '36px', height: '36px', objectFit: 'cover', borderRadius: '4px' }} />
                        <span style={{ fontSize: '12px', color: '#334155', fontWeight: '500' }}>Fundo carregado</span>
                      </div>
                      <button 
                        type="button" 
                        onClick={() => setBgImagemUrl('')}
                        style={{ background: '#fee2e2', border: 'none', color: '#ef4444', padding: '6px', borderRadius: '6px', cursor: 'pointer' }}
                        title="Remover fundo"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => fileBgRef.current?.click()}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '2px dashed #cbd5e1',
                        borderRadius: '8px',
                        backgroundColor: '#f8fafc',
                        color: '#475569',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px'
                      }}
                    >
                      <UploadCloud size={16} color="#2563eb" /> Carregar Imagem de Fundo
                    </button>
                  )}
                </div>
              )}

              {/* Upload do Logotipo */}
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
                  Logotipo Central no QR Code
                </label>

                <input 
                  type="file" 
                  ref={fileLogoRef} 
                  accept="image/*" 
                  onChange={handleUploadLogo} 
                  style={{ display: 'none' }} 
                />

                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                  {logoFinal ? (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '8px 12px',
                      background: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      flex: 1
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <img src={logoFinal} alt="Logo" style={{ width: '36px', height: '36px', objectFit: 'contain', borderRadius: '4px', background: '#fff' }} />
                        <span style={{ fontSize: '12px', color: '#334155', fontWeight: '500' }}>Logo ativo</span>
                      </div>
                      <button 
                        type="button" 
                        onClick={() => { setCustomLogo(''); setIncluirLogo(false); }}
                        style={{ background: '#fee2e2', border: 'none', color: '#ef4444', padding: '6px', borderRadius: '6px', cursor: 'pointer' }}
                        title="Remover logotipo"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => fileLogoRef.current?.click()}
                      style={{
                        flex: 1,
                        padding: '10px',
                        border: '2px dashed #cbd5e1',
                        borderRadius: '8px',
                        backgroundColor: '#f8fafc',
                        color: '#475569',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px'
                      }}
                    >
                      <UploadCloud size={16} color="#2563eb" /> Carregar Logotipo Próprio
                    </button>
                  )}
                </div>

                {logoFinal && (
                  <div style={{ marginTop: '8px' }}>
                    <SwitchToggle
                      checked={incluirLogo}
                      onChange={setIncluirLogo}
                      label="Centralizar logotipo no QR Code"
                      description="Exibe o logo no centro do código com recorte de segurança"
                      icon={<ImageIcon size={16} />}
                    />
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>

        {/* COLUNA DIREITA: Preview em Tempo Real */}
        <div style={{ position: 'sticky', top: '20px' }}>
          
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
            
            {/* 1. MODO: APENAS QR CODE */}
            {modoVisualizacao === 'qr-apenas' && (
              <div style={{
                padding: '30px',
                background: fundoQrTransparente ? 'transparent' : corFundoQr,
                backgroundImage: fundoQrTransparente ? 'linear-gradient(45deg, #eee 25%, transparent 25%), linear-gradient(-45deg, #eee 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #eee 75%), linear-gradient(-45deg, transparent 75%, #eee 75%)' : 'none',
                backgroundSize: '16px 16px',
                backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0px',
                borderRadius: '16px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 8px 20px rgba(0,0,0,0.06)',
                textAlign: 'center'
              }}>
                <div ref={qrCanvasRef}>
                  <QRCodeCanvas
                    value={qrValor}
                    size={240}
                    bgColor={fundoQrTransparente ? 'transparent' : corFundoQr}
                    fgColor={corQrCode}
                    level="H"
                    includeMargin={!fundoQrTransparente}
                    imageSettings={
                      incluirLogo && logoFinal
                        ? {
                            src: logoFinal,
                            height: 48,
                            width: 48,
                            excavate: true,
                          }
                        : undefined
                    }
                  />
                </div>
                <div style={{ marginTop: '12px', fontSize: '13px', fontWeight: '700', color: '#334155' }}>
                  {getTituloExibicao()}
                </div>
              </div>
            )}

            {/* 2. MODO: FOLHA DE ETIQUETAS ADESIVAS */}
            {modoVisualizacao === 'etiquetas' && (
              <div style={{
                width: '100%',
                maxWidth: '480px',
                maxHeight: '480px',
                overflowY: 'auto',
                padding: '16px',
                background: '#fff',
                borderRadius: '16px',
                border: '1px solid #cbd5e1',
                boxShadow: '0 8px 20px rgba(0,0,0,0.06)'
              }}>
                <div style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', marginBottom: '10px', textAlign: 'center' }}>
                  Pré-visualização: Grade de {quantidadeEtiquetas} Etiquetas
                </div>

                {/* QR oculto de referência para renderizar no canvas */}
                <div ref={qrCanvasRef} style={{ display: 'none' }}>
                  <QRCodeCanvas
                    value={qrValor}
                    size={160}
                    bgColor={fundoQrTransparente ? 'transparent' : corFundoQr}
                    fgColor={corQrCode}
                    level="H"
                    includeMargin={true}
                    imageSettings={
                      incluirLogo && logoFinal
                        ? {
                            src: logoFinal,
                            height: 32,
                            width: 32,
                            excavate: true,
                          }
                        : undefined
                    }
                  />
                </div>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
                  gap: '10px'
                }}>
                  {Array.from({ length: quantidadeEtiquetas }).map((_, idx) => (
                    <div key={idx} style={{
                      border: '1px dashed #cbd5e1',
                      borderRadius: '8px',
                      padding: '8px 6px',
                      textAlign: 'center',
                      background: '#fafafa',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {logoFinal && (
                        <img src={logoFinal} alt="Logo" style={{ maxHeight: '16px', maxWidth: '50px', objectFit: 'contain', marginBottom: '2px' }} />
                      )}
                      <div style={{ fontSize: '9px', fontWeight: '800', color: '#0f172a', textTransform: 'uppercase', marginBottom: '2px' }}>
                        {getTituloExibicao()}
                      </div>
                      <QRCodeCanvas
                        value={qrValor}
                        size={65}
                        bgColor={fundoQrTransparente ? 'transparent' : corFundoQr}
                        fgColor={corQrCode}
                        level="M"
                        includeMargin={false}
                      />
                      <div style={{ fontSize: '8px', color: '#64748b', marginTop: '2px', fontWeight: '600' }}>
                        Escaneie Aqui
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 3. MODO: PLACA DISPLAY DE MESA */}
            {modoVisualizacao === 'display' && (
              <div 
                style={{
                  width: formato === 'mesa' ? '340px' : '460px',
                  minHeight: formato === 'mesa' ? '480px' : '620px',
                  backgroundColor: fundoTransparente ? 'transparent' : corFundo,
                  backgroundImage: !fundoTransparente && bgImagemUrl ? `url(${bgImagemUrl})` : 'none',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  borderRadius: '20px',
                  border: fundoTransparente ? '2px dashed #94a3b8' : `3px solid ${corPrimaria}`,
                  padding: '30px 20px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  textAlign: 'center',
                  boxShadow: '0 12px 30px -8px rgba(0, 0, 0, 0.15)',
                  boxSizing: 'border-box',
                  position: 'relative'
                }}
              >
                {/* Header */}
                <div style={{ width: '100%' }}>
                  {logoFinal && (
                    <img 
                      src={logoFinal} 
                      alt="Logo" 
                      style={{ 
                        maxHeight: formato === 'mesa' ? '44px' : '58px', 
                        maxWidth: '150px', 
                        objectFit: 'contain', 
                        margin: '0 auto 10px auto', 
                        display: 'block' 
                      }} 
                    />
                  )}
                  
                  <h1 style={{ 
                    fontSize: formato === 'mesa' ? '20px' : '26px', 
                    fontWeight: '900', 
                    color: corPrimaria, 
                    margin: '0 0 6px 0', 
                    textTransform: 'uppercase', 
                    letterSpacing: '0.5px',
                    lineHeight: '1.2'
                  }}>
                    {getTituloExibicao()}
                  </h1>

                  <p style={{ 
                    fontSize: formato === 'mesa' ? '12px' : '14px', 
                    color: corTexto, 
                    margin: '0 auto', 
                    maxWidth: '280px', 
                    lineHeight: '1.3',
                    fontWeight: '500'
                  }}>
                    {getSubtituloExibicao()}
                  </p>
                </div>

                {/* QR Code Container */}
                <div 
                  ref={qrCanvasRef} 
                  style={{ 
                    padding: '12px', 
                    background: fundoQrTransparente ? 'transparent' : corFundoQr, 
                    borderRadius: '16px', 
                    border: fundoQrTransparente ? 'none' : '1px solid #e2e8f0', 
                    display: 'inline-block',
                    margin: '16px 0',
                    boxShadow: fundoQrTransparente ? 'none' : '0 6px 12px -2px rgba(0, 0, 0, 0.08)'
                  }}
                >
                  <QRCodeCanvas
                    value={qrValor}
                    size={formato === 'mesa' ? 180 : 220}
                    bgColor={fundoQrTransparente ? 'transparent' : corFundoQr}
                    fgColor={corQrCode}
                    level="H"
                    includeMargin={!fundoQrTransparente}
                    imageSettings={
                      incluirLogo && logoFinal
                        ? {
                            src: logoFinal,
                            height: formato === 'mesa' ? 36 : 46,
                            width: formato === 'mesa' ? 36 : 46,
                            excavate: true,
                          }
                        : undefined
                    }
                  />
                </div>

                {/* Rodapé */}
                <div style={{ width: '100%' }}>
                  {tipo === 'wifi' && mostrarTextoWifi && wifiSsid && (
                    <div style={{ 
                      marginBottom: '10px', 
                      padding: '8px 14px', 
                      background: 'rgba(255, 255, 255, 0.95)', 
                      borderRadius: '8px', 
                      border: '1px solid #e2e8f0', 
                      fontSize: '12px', 
                      color: '#1e293b',
                      display: 'inline-block',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.04)'
                    }}>
                      <div><strong>Rede:</strong> {wifiSsid}</div>
                      {wifiSenha && <div><strong>Senha:</strong> {wifiSenha}</div>}
                    </div>
                  )}

                  <div style={{ 
                    fontSize: '11px', 
                    color: corTexto, 
                    fontWeight: '600',
                    opacity: 0.85
                  }}>
                    {rodapeDisplay}
                  </div>

                  <div style={{ 
                    marginTop: '10px', 
                    borderTop: '1px dashed rgba(0,0,0,0.15)', 
                    paddingTop: '8px', 
                    fontSize: '9px', 
                    color: corTexto, 
                    opacity: 0.6 
                  }}>
                    {lojaInfo?.nome || 'Universo Mídia Digital'}
                  </div>
                </div>
              </div>
            )}

          </div>

          {/* Botões de Ação */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <button
              type="button"
              onClick={handleImprimir}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '14px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: '#2563eb',
                color: '#fff',
                fontWeight: '700',
                fontSize: '15px',
                cursor: 'pointer',
                boxShadow: '0 4px 10px rgba(37, 99, 235, 0.3)'
              }}
            >
              <Printer size={20} />
              {modoVisualizacao === 'etiquetas' ? 'Imprimir Folha de Etiquetas A4' : 'Imprimir Display / Placa'}
            </button>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <button
                type="button"
                onClick={baixarApenasPNG}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  padding: '10px',
                  borderRadius: '8px',
                  border: '1px solid #cbd5e1',
                  backgroundColor: '#ffffff',
                  color: '#334155',
                  fontWeight: '600',
                  fontSize: '13px',
                  cursor: 'pointer'
                }}
              >
                <Download size={16} /> Baixar QR (PNG)
              </button>

              <button
                type="button"
                onClick={copiarConteudo}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  padding: '10px',
                  borderRadius: '8px',
                  border: '1px solid #cbd5e1',
                  backgroundColor: '#ffffff',
                  color: '#334155',
                  fontWeight: '600',
                  fontSize: '13px',
                  cursor: 'pointer'
                }}
              >
                <Copy size={16} /> Copiar Link
              </button>
            </div>
          </div>

        </div>

      </div>
    </div>
  );
};

export default QRCodeManager;